/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

/**
 * Formats a price in cents to a currency string in dollars.
 *
 * @param cents - The price in cents (e.g., 2700 for $27.00)
 * @param currency - The currency code (default: 'USD')
 * @param locale - The locale for formatting (default: 'en-US')
 * @returns Formatted currency string (e.g., '$27.00')
 *
 * @example
 * formatCurrency(2700) // Returns '$27.00'
 * formatCurrency(0) // Returns '$0.00'
 * formatCurrency(12345) // Returns '$123.45'
 */
export const formatCurrency = (
  cents: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string => {
  // Convert cents to dollars
  const dollars = cents / 100;

  // Use Intl.NumberFormat for proper currency formatting
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
};

/**
 * Formats a price in cents to a simple dollar string without currency symbol.
 *
 * @param cents - The price in cents
 * @returns Formatted price string (e.g., '27.00')
 *
 * @example
 * formatPrice(2700) // Returns '27.00'
 * formatPrice(0) // Returns '0.00'
 */
export const formatPrice = (cents: number): string => {
  const dollars = cents / 100;
  return dollars.toFixed(2);
};

/**
 * Formats a monthly price with billing cycle information.
 *
 * @param cents - The monthly price in cents
 * @param billingCycle - The billing cycle ('monthly' | 'yearly')
 * @returns Formatted price string with billing info
 *
 * @example
 * formatMonthlyPrice(2700, 'monthly') // Returns '$27.00/month'
 * formatMonthlyPrice(2700, 'yearly') // Returns '$27.00/month • Billed yearly'
 */
export const formatMonthlyPrice = (
  cents: number,
  billingCycle: 'monthly' | 'yearly' = 'monthly'
): string => {
  const formattedPrice = formatCurrency(cents);

  if (billingCycle === 'yearly') {
    return `${formattedPrice}/month • Billed yearly`;
  }

  return `${formattedPrice}/month`;
};
